import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useState, useMemo, useCallback, useEffect } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { Tabs, Spinner, Button, Icon } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';
import { ApplicationsTable } from './components/ApplicationsTable';
import { ApplicationFilters } from './components/ApplicationFilters';
import { StyledMobileHeaderWrapper } from 'admin/components/MobileLayout/style';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import { LeftOutlined } from '@ant-design/icons';
import type { ListingApplication, Listing } from 'types/ListingData';
import {
  ApplicationFilterState,
  ExtendedApplicationProductData,
} from './types';
import { StyledApplicationsPageWrapper } from './styles';
import { adminPath } from 'enums/adminRoutes';

export const ListingApplicationsPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { listingId } = useParams<{ listingId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilterState] = useState<ApplicationFilterState>({});
  const [openView, setOpenView] = useState<boolean>(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const {
    data: listing,
    isLoading: isLoadingListing,
    refetch: refetchListing,
  } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/${listingId}`,
    },
  });

  const processedFilterState = useMemo(() => {
    const processed: any = { ...filterState };
    if (processed.show_only_required_quantities !== undefined) {
      processed.show_only_required_quantities =
        processed.show_only_required_quantities ? 1 : 0;
    }
    return processed;
  }, [filterState]);

  const {
    data: applicationsData,
    isLoading: isLoadingApplications,
    refetch,
  } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/${listingId}/applications`,
      data: processedFilterState,
      disableOnMount: true,
    },
  });

  useEffect(() => {
    refetch();
  }, [processedFilterState]);

  const applications: ListingApplication[] = useMemo(() => {
    if (!applicationsData || !Array.isArray(applicationsData)) {
      return [];
    }
    return applicationsData;
  }, [applicationsData]);

  const listingData = listing as Listing | undefined;
  const isSingleProducerOnly = listingData?.single_producer_only === true;

  const bestValues = useMemo(() => {
    if (!applications.length) {
      return null;
    }

    const allValues = {
      unitPrices: [] as number[],
      totalPrices: [] as number[],
      applicationTotalPrices: [] as number[],
      distances: [] as number[],
      applicationQuantities: [] as number[],
      productQuantities: [] as number[],
    };

    applications.forEach((application, appIndex) => {
      const appDistance =
        typeof application.calculated_distance_km === 'string'
          ? parseFloat(application.calculated_distance_km)
          : application.calculated_distance_km || 0;
      if (appDistance > 0) {
        allValues.distances.push(appDistance);
      }

      const appTotalPrice =
        typeof application.total_offer_price === 'string'
          ? parseFloat(application.total_offer_price)
          : application.total_offer_price || 0;
      if (appTotalPrice > 0) {
        allValues.applicationTotalPrices.push(appTotalPrice);
      }

      const appTotalQuantity = (application.application_products || []).reduce(
        (sum, product) => {
          const requiredQty = product.listing_product?.required_quantity;
          const quantity =
            product.offered_quantity ??
            (typeof requiredQty === 'string'
              ? parseFloat(requiredQty)
              : requiredQty) ??
            0;
          return sum + quantity;
        },
        0
      );
      if (appTotalQuantity > 0) {
        allValues.applicationQuantities.push(appTotalQuantity);
      }

      application.application_products?.forEach((product, prodIndex) => {
        const requiredQty = product.listing_product?.required_quantity;
        const quantity =
          product.offered_quantity ??
          (typeof requiredQty === 'string'
            ? parseFloat(requiredQty)
            : requiredQty) ??
          0;

        const unitPrice = product.offered_price ?? 0;

        if (unitPrice > 0) {
          allValues.unitPrices.push(unitPrice);
        }

        const totalPrice =
          quantity > 0 && unitPrice > 0 ? quantity * unitPrice : 0;

        if (totalPrice > 0) {
          allValues.totalPrices.push(totalPrice);
        }

        if (quantity > 0) {
          allValues.productQuantities.push(quantity);
        }
      });
    });

    const minIfVaried = (arr: number[]) => {
      if (arr.length === 0) return null;
      const min = Math.min(...arr);
      const max = Math.max(...arr);
      return min !== max ? min : null;
    };
    const maxIfVaried = (arr: number[]) => {
      if (arr.length === 0) return null;
      const min = Math.min(...arr);
      const max = Math.max(...arr);
      return min !== max ? max : null;
    };

    const result: {
      minUnitPrice: number | null;
      minTotalPrice: number | null;
      minApplicationTotalPrice: number | null;
      minDistance: number | null;
      maxApplicationQuantity: number | null;
      maxProductQuantity: number | null;
      productQuantityByListingProduct?: Record<
        string,
        { minQuantity: number; maxQuantity: number }
      >;
      productBestValuesByListingProduct?: Record<
        string,
        { minUnitPrice: number | null; minTotalPrice: number | null }
      >;
    } = {
      minUnitPrice: minIfVaried(allValues.unitPrices),
      minTotalPrice: minIfVaried(allValues.totalPrices),
      minApplicationTotalPrice: minIfVaried(allValues.applicationTotalPrices),
      minDistance: minIfVaried(allValues.distances),
      maxApplicationQuantity: maxIfVaried(allValues.applicationQuantities),
      maxProductQuantity: maxIfVaried(allValues.productQuantities),
    };

    if (isSingleProducerOnly) {
      const quantityByListingProduct = new Map<
        string,
        { applicationId: number; quantity: number }[]
      >();
      const unitPricesByListingProduct = new Map<string, number[]>();
      const totalPricesByListingProduct = new Map<string, number[]>();

      applications.forEach((application) => {
        application.application_products?.forEach(
          (product: ExtendedApplicationProductData) => {
            const listingProductId = String(
              product.listing_product_id ?? product.listing_product?.id ?? ''
            );
            if (!listingProductId) return;
            const requiredQty = product.listing_product?.required_quantity;
            const quantity =
              product.offered_quantity ??
              (typeof requiredQty === 'string'
                ? parseFloat(requiredQty)
                : requiredQty) ??
              0;
            const unitPrice = product.offered_price ?? 0;
            const totalPrice =
              quantity > 0 && unitPrice > 0 ? quantity * unitPrice : 0;

            if (quantity > 0) {
              if (!quantityByListingProduct.has(listingProductId)) {
                quantityByListingProduct.set(listingProductId, []);
              }
              quantityByListingProduct.get(listingProductId)!.push({
                applicationId: application.id!,
                quantity,
              });
            }
            if (unitPrice > 0) {
              if (!unitPricesByListingProduct.has(listingProductId)) {
                unitPricesByListingProduct.set(listingProductId, []);
              }
              unitPricesByListingProduct.get(listingProductId)!.push(unitPrice);
            }
            if (totalPrice > 0) {
              if (!totalPricesByListingProduct.has(listingProductId)) {
                totalPricesByListingProduct.set(listingProductId, []);
              }
              totalPricesByListingProduct
                .get(listingProductId)!
                .push(totalPrice);
            }
          }
        );
      });

      const productQuantityByListingProduct: Record<
        string,
        { minQuantity: number; maxQuantity: number }
      > = {};
      quantityByListingProduct.forEach((entries, listingProductId) => {
        const quantities = entries.map((e) => e.quantity);
        const min = Math.min(...quantities);
        const max = Math.max(...quantities);
        if (min !== max) {
          productQuantityByListingProduct[listingProductId] = {
            minQuantity: min,
            maxQuantity: max,
          };
        }
      });
      result.productQuantityByListingProduct = productQuantityByListingProduct;

      const productBestValuesByListingProduct: Record<
        string,
        { minUnitPrice: number | null; minTotalPrice: number | null }
      > = {};
      const allListingProductIds = new Set([
        ...Array.from(unitPricesByListingProduct.keys()),
        ...Array.from(totalPricesByListingProduct.keys()),
      ]);
      allListingProductIds.forEach((listingProductId) => {
        const unitPrices =
          unitPricesByListingProduct.get(listingProductId) ?? [];
        const totalPrices =
          totalPricesByListingProduct.get(listingProductId) ?? [];
        productBestValuesByListingProduct[listingProductId] = {
          minUnitPrice: minIfVaried(unitPrices),
          minTotalPrice: minIfVaried(totalPrices),
        };
      });
      result.productBestValuesByListingProduct =
        productBestValuesByListingProduct;
    }

    return result;
  }, [applications, isSingleProducerOnly]);

  const handleExpandedRowsChange = (keys: readonly React.Key[]) => {
    setExpandedRowKeys([...keys]);
  };

  const { appendData: createTransactions } = useQueryApiClient({
    request: {
      url: '/api/v2/listing-applications/create-transactions',
      method: 'POST',
    },
    onSuccess: (response: {
      transactions_created: number;
      transaction_ids: number[];
      parent_transaction_id: number | null;
    }) => {
      refetch();
      refetchListing();

      if (response.transactions_created > 1 && response.parent_transaction_id) {
        navigate(
          `${adminPath.farmTransactions()}?tab=my_orders&motherTransaction=${response.parent_transaction_id}`
        );
      } else if (response.transactions_created === 1) {
        navigate(
          `${adminPath.farmTransactions()}?tab=my_orders&transaction=${response.transaction_ids[0]}`
        );
      }
    },
    onError: (error) => {},
  });

  const handleCreateTransactions = useCallback(
    async (data: { listing_id: string }) => {
      await createTransactions(data);
    },
    [createTransactions]
  );

  const handleOpenViewChange = (checked: boolean) => {
    setOpenView(checked);
    if (checked) {
      const listingData = listing as Listing | undefined;
      const isSingleProducerOnly = listingData?.single_producer_only === true;

      if (!isSingleProducerOnly) {
        const categoryKeys = new Set<string>();
        applications.forEach((application) => {
          application.application_products?.forEach(
            (product: ExtendedApplicationProductData) => {
              const categoryId = product.listing_product?.category?.id;
              const categoryName =
                product.listing_product?.name ||
                product.listing_product?.category?.name ||
                '';
              const categoryKey = `category-${categoryId || categoryName}`;
              categoryKeys.add(categoryKey);
            }
          );
        });
        setExpandedRowKeys(Array.from(categoryKeys));
      } else {
        const allKeys = applications.map((app) => app.id);
        setExpandedRowKeys(allKeys);
      }
    } else {
      setExpandedRowKeys([]);
    }
  };

  const handleFilterSubmit = useCallback((values: ApplicationFilterState) => {
    const processedValues: ApplicationFilterState = {
      ...values,
      show_only_required_quantities:
        values.show_only_required_quantities ?? false,
    };

    setFilterState(processedValues);
  }, []);

  const applicationIdParam = searchParams.get('applicationId');
  const initialApplicationId = applicationIdParam
    ? parseInt(applicationIdParam, 10)
    : null;
  const hasValidInitialApplicationId =
    initialApplicationId !== null && !isNaN(initialApplicationId);

  const handleInitialApplicationViewed = useCallback(() => {
    if (searchParams.get('applicationId')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('applicationId');
      setSearchParams(newSearchParams);
    }
  }, [searchParams, setSearchParams]);

  const tabLabel = useMemo(() => {
    if (!listing) return '';
    const listingName = (listing as Listing).title || '';
    return `${listingName} (${intl.formatMessage({ id: 'navigation.listings' })})`;
  }, [listing, intl]);

  if (isLoadingListing && !listing) {
    return (
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
            href: '/',
          },
          {
            title: intl.formatMessage({ id: 'navigation.listings' }),
            href: adminPath.listings(),
          },
          {
            title: intl.formatMessage({ id: 'listings.applications' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <Spinner spinning={true} dontRender>
            <div />
          </Spinner>
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    );
  }

  return (
    <>
      <StyledMobileHeaderWrapper>
        <Button
          type={'text'}
          label={intl.formatMessage({ id: 'listings.back_to_listings' })}
          onClick={() => navigate(adminPath.listings())}
          icon={<LeftOutlined />}
        />
        <Button
          type={'text'}
          label={intl.formatMessage({ id: 'general.filter' })}
          onClick={() => setIsMobileFilterOpen(true)}
          icon={
            <Icon
              name={'admin-filter-icon'}
              className="admin-header-item-icon"
            />
          }
        />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
            href: '/',
          },
          {
            title: intl.formatMessage({ id: 'navigation.listings' }),
            href: adminPath.listings(),
          },
          {
            title: intl.formatMessage({ id: 'listings.applications' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <Tabs
            items={[
              {
                key: 'applications',
                label: tabLabel,
                children: (
                  <StyledApplicationsPageWrapper>
                    <div className="hide-mobile-filter">
                      <ApplicationFilters
                        onFinish={handleFilterSubmit}
                        onOpenViewChange={handleOpenViewChange}
                        openView={openView}
                        listing={listingData}
                      />
                    </div>
                    <ApplicationsTable
                      applications={applications}
                      expandedRowKeys={expandedRowKeys}
                      onExpandedRowsChange={handleExpandedRowsChange}
                      loading={
                        isLoadingApplications && applications.length === 0
                      }
                      filter={filterState}
                      page_size={25}
                      listing={listingData}
                      onActionCompleted={() => {
                        refetch();
                        refetchListing();
                      }}
                      onCreateTransactions={handleCreateTransactions}
                      isSingleProducerOnly={isSingleProducerOnly}
                      bestValues={bestValues}
                      initialApplicationId={
                        hasValidInitialApplicationId
                          ? initialApplicationId
                          : null
                      }
                      onInitialApplicationViewed={
                        handleInitialApplicationViewed
                      }
                    />
                  </StyledApplicationsPageWrapper>
                ),
              },
            ]}
          />
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
      <AdminMobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
      >
        <ApplicationFilters
          onFinish={(values) => {
            handleFilterSubmit(values);
            setIsMobileFilterOpen(false);
          }}
          onOpenViewChange={handleOpenViewChange}
          openView={openView}
          listing={listingData}
        />
      </AdminMobileFilterDrawer>
    </>
  );
};
